import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { API_ENDPOINTS, getImageUrl } from '../../utils/GlobalAPI';
import Pagination from './Pagination';
import AdminPageHeader from './AdminPageHeader';
import { FaUserFriends, FaInstagram, FaTrash, FaEye, FaExternalLinkAlt, FaCheck, FaTimes, FaSearch, FaFilter, FaSyncAlt } from 'react-icons/fa';
import { sendApprovalNotification, sendRejectionNotification } from '../../utils/emailService';

const statusColors = {
  approved: 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200',
  rejected: 'bg-gradient-to-r from-red-100 to-pink-100 text-red-800 border border-red-200',
  pending: 'bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 border border-orange-200',
  deleted: 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200',
};

const statusOptions = ['pending', 'approved', 'rejected', 'deleted'];

const AdmnInfluncerReq = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [updating, setUpdating] = useState({});
  const [deleting, setDeleting] = useState({});
  const [modalImg, setModalImg] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Soft delete: set status to 'deleted'
  const handleSoftDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete (deactivate) this influencer request?')) return;
    setDeleting((d) => ({ ...d, [id]: true }));
    try {
      const res = await fetch(`${API_ENDPOINTS.INFLUENCER}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'deleted' }),
      });
      if (!res.ok) throw new Error('Failed to delete');
      await fetchRequests();
    } catch (err) {
      alert('Failed to delete');
    } finally {
      setDeleting((d) => ({ ...d, [id]: false }));
    }
  };

  // Reactivate: set status back to 'pending'
  const handleReactivate = async (id) => {
    setUpdating((u) => ({ ...u, [id]: true }));
    try {
      const res = await fetch(`${API_ENDPOINTS.INFLUENCER}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'pending' }),
      });
      if (!res.ok) throw new Error('Failed to reactivate');
      await fetchRequests();
    } catch (err) {
      alert('Failed to reactivate');
    } finally {
      setUpdating((u) => ({ ...u, [id]: false }));
    }
  };

  const handleStatusChange = async (id, status) => {
    setUpdating((u) => ({ ...u, [id]: true }));
    try {
      const res = await fetch(`${API_ENDPOINTS.INFLUENCER}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Failed to update status');
      
      const updatedInfluencer = await res.json();
      
      // Send email notification based on status
      try {
        if (status === 'approved') {
          await sendApprovalNotification(updatedInfluencer);
          console.log('✅ Approval notification email sent');
        } else if (status === 'rejected') {
          await sendRejectionNotification(updatedInfluencer);
          console.log('✅ Rejection notification email sent');
        }
      } catch (emailError) {
        console.error('❌ Failed to send status email:', emailError);
        // Don't fail status update if email fails
      }
      
      await fetchRequests();
    } catch (err) {
      alert('Failed to update status');
    } finally {
      setUpdating((u) => ({ ...u, [id]: false }));
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.INFLUENCER);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setRequests(data.reverse());
    } catch (err) {
      setError(err.message || 'Error fetching requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const filtered = requests.filter((r) => {
    const matchesStatus = filter === 'all' || r.status === filter;
    const matchesSearch =
      r.name.toLowerCase().includes(search.toLowerCase()) ||
      r.email.toLowerCase().includes(search.toLowerCase()) ||
      r.phone.toLowerCase().includes(search.toLowerCase()) ||
      r.instagram.toLowerCase().includes(search.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Pagination logic
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRequests = filtered.slice(startIndex, endIndex);

  const handlePageChange = (page, newItemsPerPage = itemsPerPage) => {
    setCurrentPage(page);
    if (newItemsPerPage !== itemsPerPage) {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1);
    }
  };

  // Dashboard stats
  const total = requests.length;
  const approved = requests.filter(r => r.status === 'approved').length;
  const pending = requests.filter(r => r.status === 'pending').length;
  const rejected = requests.filter(r => r.status === 'rejected').length;
  const deletedCount = requests.filter(r => r.status === 'deleted').length;

  return (
    <div className="min-h-screen bg-slate-50/50">
      <AdminPageHeader
        title="Creator Request Queue"
        subtitle="Review and process creator applications for the LeadsMela network"
        icon={<FaUserFriends className="w-6 h-6" />}
      />

      <div className="pt-32 p-6 max-w-7xl mx-auto space-y-6">
        {/* Modal for Full Image Preview */}
        {modalImg && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
            <div className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-[90vw] max-h-[85vh] flex flex-col items-center border border-slate-100">
              <button
                className="absolute -top-3 -right-3 bg-white text-slate-500 hover:text-red-500 shadow-md border border-slate-100 hover:border-red-100 rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold transition-all"
                onClick={() => setModalImg(null)}
                aria-label="Close"
              >
                &times;
              </button>
              <img
                src={getImageUrl(modalImg)}
                alt="Receipt Full Preview"
                className="max-h-[70vh] rounded-xl object-contain shadow-sm border border-slate-100"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/400x300?text=Receipt+Not+Found';
                }}
              />
              <div className="mt-4 text-sm text-slate-500 font-medium">Verification Invoice / Receipt</div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-2 md:grid-cols-5 gap-4"
        >
          {[
            { label: 'Total Requests', val: total, color: 'text-slate-800', bg: 'bg-white border-slate-100' },
            { label: 'Awaiting Action', val: pending, color: 'text-amber-600', bg: 'bg-white border-amber-100' },
            { label: 'Approved Creators', val: approved, color: 'text-emerald-600', bg: 'bg-white border-emerald-100' },
            { label: 'Rejected Applications', val: rejected, color: 'text-rose-600', bg: 'bg-white border-rose-100' },
            { label: 'Deactivated Accounts', val: deletedCount, color: 'text-slate-400', bg: 'bg-white border-slate-200' }
          ].map((stat, i) => (
            <div key={i} className={`border rounded-2xl p-5 shadow-sm hover:shadow transition-shadow ${stat.bg}`}>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{stat.label}</div>
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.val}</div>
            </div>
          ))}
        </motion.div>

        {/* Unified Search & Control Bar */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
          className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col md:flex-row gap-4 items-center justify-between"
        >
          {/* Search Input */}
          <div className="relative w-full md:max-w-md">
            <FaSearch className="absolute left-3 top-3.5 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by name, email, phone or Instagram..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-slate-800 text-sm focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition placeholder:text-slate-400"
            />
          </div>

          {/* Filter Status */}
          <div className="flex items-center space-x-3 w-full md:w-auto">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 whitespace-nowrap">
              <FaFilter className="w-3.5 h-3.5 text-slate-400" />
              Filter Queue:
            </span>
            <select
              value={filter}
              onChange={e => setFilter(e.target.value)}
              className="w-full md:w-44 border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition bg-white"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>
        </motion.div>

        {/* Data Table */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white border border-slate-100 rounded-2xl shadow-sm">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mb-4"></div>
            <p className="text-sm text-slate-500">Retrieving queue entries...</p>
          </div>
        ) : error ? (
          <div className="text-center text-rose-600 py-10 bg-white border border-slate-100 rounded-2xl shadow-sm">{error}</div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="space-y-4"
          >
            <div className="overflow-x-auto bg-white rounded-2xl border border-slate-100 shadow-sm">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Influencer</th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Instagram</th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Verification Receipt</th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">ID Metadata</th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                    <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {paginatedRequests.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-16 text-slate-500">
                        <div className="flex flex-col items-center">
                          <div className="w-14 h-14 bg-slate-50 rounded-full flex items-center justify-center mb-3 border border-slate-100">
                            <FaUserFriends className="w-6 h-6 text-slate-400" />
                          </div>
                          <p className="text-base font-semibold text-slate-700">No applications found</p>
                          <p className="text-xs text-slate-400 mt-1">Try adjusting your filters or search keywords</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedRequests.map((r) => (
                      <tr key={r._id} className="hover:bg-slate-50/40 transition-colors group">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            {r.influencerImage ? (
                              <img
                                src={getImageUrl(r.influencerImage)}
                                alt={r.name}
                                className="w-9 h-9 rounded-full object-cover border border-slate-100 shadow-sm"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23cbd5e1'%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E";
                                }}
                              />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center text-white font-bold text-sm shadow-sm flex-shrink-0">
                                {r.name ? r.name.charAt(0).toUpperCase() : 'C'}
                              </div>
                            )}
                            <div>
                              <div className="font-semibold text-slate-800 text-sm">{r.name}</div>
                              <div className="text-[11px] text-slate-400 font-mono mt-0.5">{r.phone}</div>
                              <div className="text-[11px] text-slate-400 mt-0.5">{r.email}</div>
                            </div>
                          </div>
                        </td>

                        {/* Instagram Column */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-1">
                            <div>
                              <a 
                                href={r.instagram} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border border-pink-200/50 text-pink-700 rounded-full text-xs font-semibold transition-all duration-200"
                              >
                                <FaInstagram className="w-3.5 h-3.5 text-pink-600" />
                                <span>Profile Link</span>
                                <FaExternalLinkAlt className="w-2.5 h-2.5 opacity-65" />
                              </a>
                            </div>
                            <div>
                              <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${
                                r.influencerType === 'Featured Influencer'
                                  ? 'bg-amber-50 text-amber-700 border border-amber-200/40'
                                  : 'bg-slate-50 text-slate-600 border border-slate-200/40'
                              }`}>
                                {r.influencerType}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Receipt Screenshot Column */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {r.screenshot ? (
                            <div className="flex items-center space-x-2">
                              <div 
                                className="relative group/thumb cursor-pointer overflow-hidden rounded-lg border border-slate-200 hover:border-orange-400 shadow-sm transition-all duration-200 w-11 h-11"
                                onClick={() => setModalImg(r.screenshot)}
                              >
                                <img 
                                  src={getImageUrl(r.screenshot)}
                                  alt="Receipt Thumbnail" 
                                  className="w-full h-full object-cover transition duration-300 group-hover/thumb:scale-105"
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/48x48?text=Err';
                                  }}
                                />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center">
                                  <FaEye className="w-3.5 h-3.5 text-white" />
                                </div>
                              </div>
                              <span className="text-[10px] text-slate-400 font-medium hidden lg:inline">Click to view</span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-xs italic">No invoice uploaded</span>
                          )}
                        </td>

                        {/* Metadata IDs Column */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-1">
                            <div className="text-[10px] font-mono text-slate-600 bg-slate-50 border border-slate-100 rounded px-1.5 py-0.5 inline-block max-w-[150px] truncate" title={r.transactionId}>
                              TXN: {r.transactionId}
                            </div>
                            {r.influencerId && (
                              <div className="text-[11px] font-bold text-slate-700 pl-0.5">
                                UID: <span className="font-mono text-slate-500 font-medium">{r.influencerId}</span>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Status Column */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm border ${
                            r.status === 'approved'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : r.status === 'pending'
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : r.status === 'rejected'
                              ? 'bg-rose-50 text-rose-700 border-rose-200'
                              : 'bg-slate-50 text-slate-500 border-slate-200'
                          }`}>
                            {r.status.toUpperCase()}
                          </span>
                        </td>

                        {/* Instant Action Column */}
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-1.5">
                            {r.status === 'deleted' ? (
                              <button
                                onClick={() => handleReactivate(r._id)}
                                disabled={updating[r._id]}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 text-emerald-700 text-xs font-bold rounded-lg transition-all"
                              >
                                <FaSyncAlt className="w-3 h-3" />
                                <span>Reactivate</span>
                              </button>
                            ) : (
                              <>
                                {/* Quick Process Controls if Pending */}
                                {r.status === 'pending' && (
                                  <>
                                    <button
                                      onClick={() => handleStatusChange(r._id, 'approved')}
                                      disabled={updating[r._id]}
                                      className="p-1.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 hover:border-emerald-300 text-emerald-700 rounded-lg transition-all"
                                      title="Approve Creator"
                                    >
                                      <FaCheck className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleStatusChange(r._id, 'rejected')}
                                      disabled={updating[r._id]}
                                      className="p-1.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 hover:border-rose-300 text-rose-700 rounded-lg transition-all"
                                      title="Reject Creator"
                                    >
                                      <FaTimes className="w-3.5 h-3.5" />
                                    </button>
                                  </>
                                )}

                                {/* Reverse/Reset status trigger if already Processed */}
                                {r.status !== 'pending' && (
                                  <button
                                    onClick={() => handleStatusChange(r._id, 'pending')}
                                    disabled={updating[r._id]}
                                    className="px-2.5 py-1 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 rounded-lg text-xs font-semibold transition"
                                    title="Reset Application to Pending"
                                  >
                                    Reset to Review
                                  </button>
                                )}

                                {/* Delete Account Trigger */}
                                <button
                                  onClick={() => handleSoftDelete(r._id)}
                                  disabled={deleting[r._id]}
                                  className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg border border-transparent hover:border-rose-100 transition-all"
                                  title="Deactivate Creator"
                                >
                                  <FaTrash className="w-3.5 h-3.5" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              totalItems={filtered.length}
              showItemsPerPage={true}
              itemsPerPageOptions={[5, 10, 20, 50]}
            />
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdmnInfluncerReq;